import React, { useState, useRef, useEffect } from 'react';
import { MovieActions } from './MovieActions';
import { PCRBadges } from './PCRBadges';

export const MovieRow = ({
  movie,
  fileErrors,
  isAnyProcessing,
  onAnalyzeFull,
  onAnalyzeDeepFocus,
  canUsePCR,
  onAnalyzePCR,
  onDeleteMovie,
  onRemoveFailedMovie,
  onUpdateScriptType, // New prop for updating script type
  showSuccess,
  showError,
  getStatusBadge,
  getRowClassName,
  user, // Add this prop
}) => {
  const [isUpdatingType, setIsUpdatingType] = useState(false);

  // Full per-model PCRH (the complete verdicts — not the legacy PCR projection).
  // Server-rendered by BatchController::pcrh_view reusing the analysis page's
  // per-model partials, which carry their own CSS + a window.pcrhOpenModelModal
  // script. innerHTML injection does NOT run <script> tags, so we re-create them
  // after injecting. The partials resolve panels by a slug-based element id, so
  // we inject only while THIS row's modal is open and clear on close — otherwise
  // two rows' panels would collide in the DOM.
  const pcrhBodyRef = useRef(null);
  const pcrhModalRef = useRef(null);
  const [pcrhHtml, setPcrhHtml] = useState(null);
  const [pcrhLoading, setPcrhLoading] = useState(false);
  const [pcrhError, setPcrhError] = useState(null);

  const injectPcrh = (html) => {
    const el = pcrhBodyRef.current;
    if (!el || !html) return;
    el.innerHTML = html;
    // Re-create scripts so they actually execute (defines pcrhOpenModelModal etc.).
    el.querySelectorAll('script').forEach((old) => {
      const s = document.createElement('script');
      if (old.src) s.src = old.src;
      else s.textContent = old.textContent;
      old.replaceWith(s);
    });
  };

  const loadPcrh = async () => {
    if (pcrhHtml) { injectPcrh(pcrhHtml); return; } // re-inject cached on re-open
    if (pcrhLoading) return;
    setPcrhLoading(true);
    setPcrhError(null);
    try {
      const res = await fetch(`/analysis/pcrh/${movie.id}`, { headers: { 'Accept': 'text/html' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      setPcrhHtml(html);
      injectPcrh(html);
    } catch (e) {
      console.error('Error loading PCRH details:', e);
      setPcrhError('Could not load the PCRH details.');
    } finally {
      setPcrhLoading(false);
    }
  };

  // Clear the injected PCRH fragment when its modal closes, so only the open
  // row's panels are ever in the DOM (avoids duplicate slug-id collisions).
  useEffect(() => {
    const modalEl = pcrhModalRef.current;
    if (!modalEl) return;
    const onHidden = () => { if (pcrhBodyRef.current) pcrhBodyRef.current.innerHTML = ''; };
    modalEl.addEventListener('hidden.bs.modal', onHidden);
    return () => modalEl.removeEventListener('hidden.bs.modal', onHidden);
  }, []);

  const getPCRScore = (pcrList) => {
    if (!pcrList) return null;
    
    let numberOfScores = 0;
    let pcrScore = 0;
    
    Object.keys(pcrList).forEach(key => {
      if (pcrList[key].score) {
        pcrScore += pcrList[key].score;
        numberOfScores++;
      }
    });
    
    if (numberOfScores > 0) {
      return (pcrScore / numberOfScores).toFixed(1);
    }
    
    return null;
  };
  
  const handleScriptTypeChange = async (newType) => {
    if (isUpdatingType || newType === movie.script_type) return;
    
    setIsUpdatingType(true);
    
    try {
      const response = await fetch(`/movies/${movie.id}/script-type`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          script_type: newType
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Update movie object via parent component
        if (onUpdateScriptType) {
          onUpdateScriptType(movie.id, newType);
        }
        showSuccess && showSuccess('Script type updated successfully');
      }
    } catch (error) {
      console.error('Error updating script type:', error);
      showError && showError(`Failed to update script type: ${error.message}`);
    } finally {
      setIsUpdatingType(false);
    }
  };

  return (
    <tr key={movie.id} className={getRowClassName(movie)}>
      <td className="fw-semibold" style={{ maxWidth: '200px' }}>
        <span
          className="d-inline-block text-truncate"
          style={{ maxWidth: '100%', wordBreak: 'break-all' }}
          title={movie.title || 'Untitled'}
        >
          {movie.title || 'Untitled'}
        </span>
      </td>

      <td style={{ width: '120px' }}>
        <select
          className="form-select form-select-sm"
          value={movie.script_type || 'feature'}
          onChange={(e) => handleScriptTypeChange(e.target.value)}
          disabled={isUpdatingType || isAnyProcessing || movie.status !== 'uploaded'}
          style={{ fontSize: '0.8rem' }}
        >
          <option value="feature">Feature</option>
          <option value="short">Short</option>
          <option value="tv_pilot">TV Pilot</option>
          <option value="tv_episode">TV Episode</option>
          <option value="other">Other</option>
        </select>
        {isUpdatingType && (
          <div className="spinner-border spinner-border-sm mt-1" role="status">
            <span className="visually-hidden">Updating...</span>
          </div>
        )}
      </td>

      <td className="small" style={{ fontSize: '0.8rem' }}>
        {movie.createdAt ? new Date(movie.createdAt).toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }) : 'N/A'}
      </td>

      <td>{getStatusBadge(movie.status, movie.id)}</td>

      <td><PCRBadges ratings={movie?.pcr_ratings} />
{ ((movie.pcr_ratings && movie.pcr_ratings.length > 0) ||
   (movie.pcrh_list && (typeof movie.pcrh_list === 'string' ? movie.pcrh_list.length > 2 : Object.keys(movie.pcrh_list).length > 0)) ||
   (movie.pcr_list && typeof movie.pcr_list === 'object' && Object.keys(movie.pcr_list).length > 0)) ? (
       <button
        type="button"
        className="btn btn-light btn-sm border ms-2"
        data-bs-toggle="modal"
        data-bs-target={`#pcrh-full-${movie.id}`}
        onClick={loadPcrh}
        title="Open full PCRH details"
        style={{ lineHeight: 1, padding: '2px 6px' }}
      >Details
      </button>
        ) : (
            <span className="text-muted">—</span>
        )}
</td>

      <td className="text-center" style={{ width: '80px' }}>
        {(() => {
          const score = getPCRScore(movie.pcr_list);
          return score ? (
            <span title={`Average PCR Score: ${score}`}>
              {score}
            </span>
          ) : (
            <span className="text-muted">—</span>
          );
        })()}
      </td>

<td style={{ maxWidth: '250px' }}>
  <div className="d-flex flex-wrap gap-1">
    {/* Synthesis entry point — opens the unified tabbed PCRH modal on its
        Synthesis tab (same modal as the Details button). */}
    {movie.has_synthesis && (
      <button
        type="button"
        className="btn btn-primary btn-sm"
        data-bs-toggle="modal"
        data-bs-target={`#pcrh-full-${movie.id}`}
        onClick={loadPcrh}
        title="View the PCRH synthesis + per-reader verdicts"
        style={{ fontSize: '0.75rem', padding: '2px 6px' }}
      >
        Synthesis
      </button>
    )}

    {/* Executive Summary Button */}
    {(movie.summaries?.executive?.content || movie.summaries?.writer?.content || movie.pcr_exec_summary) && (
      <button
        type="button"
        className="btn btn-outline-primary btn-sm"
        data-bs-toggle="modal"
        data-bs-target={`#exec-${movie.id}`}
        title="View executive summaries"
        style={{ fontSize: '0.75rem', padding: '2px 6px' }}
      >
        Exec Summary
      </button>
    )}

    {/* Synopsis Button */}
    {movie.synopsis && (
      <button
        type="button"
        className="btn btn-outline-info btn-sm"
        data-bs-toggle="modal"
        data-bs-target={`#synopsis-${movie.id}`}
        title="View synopsis"
        style={{ fontSize: '0.75rem', padding: '2px 6px' }}
      >
        Synopsis
      </button>
    )}
    
    {/* Logline Button */}
    {(movie.loglines?.logline || movie.loglines?.alternate) && (
      <button
        type="button"
        className="btn btn-outline-success btn-sm"
        data-bs-toggle="modal"
        data-bs-target={`#logline-${movie.id}`}
        title="View loglines"
        style={{ fontSize: '0.75rem', padding: '2px 6px' }}
      >
        Logline{movie.loglines?.alternate ? 's' : ''}
      </button>
    )}
    
    {/* Show dash if no data available */}
    {!movie.summaries?.executive?.content && 
     !movie.summaries?.writer?.content && 
     !movie.pcr_exec_summary && 
     !movie.synopsis && 
     !movie.loglines?.logline && 
     !movie.loglines?.alternate && 
     (!movie.errorPreview || (movie.status !== 'failed' && !movie.status?.includes('_failed'))) && (
      <span className="text-muted">—</span>
    )}
    
    {/* Show error preview for failed uploads */}
    {movie.errorPreview && (movie.status === 'failed' || (movie.status && movie.status.includes('_failed'))) && (
      <span className="text-danger small d-inline-block text-truncate" style={{ maxWidth: '100%' }}>
        {movie.errorPreview}
      </span>
    )}
  </div>
  
  {/* Modals */}

  {/* Executive Summary Modal */}
  {(movie.summaries?.executive?.content || movie.summaries?.writer?.content || movie.pcr_exec_summary) && (
    <div className="modal fade" id={`exec-${movie.id}`} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Executive Summary - {movie.title}</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
          </div>
          <div className="modal-body">
            {movie.summaries?.executive?.content && (
              <div className="mb-4">
                <h6 className="fw-bold border-bottom pb-1 mb-2 text-primary">
                  {movie.summaries.executive.label || 'For Executives'}
                </h6>
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {movie.summaries.executive.content}
                </div>
              </div>
            )}
            {movie.summaries?.writer?.content && (
              <div className="mb-4">
                <h6 className="fw-bold border-bottom pb-1 mb-2 text-success">
                  {movie.summaries.writer.label || 'For Writers'}
                </h6>
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {movie.summaries.writer.content}
                </div>
              </div>
            )}
            {movie.pcr_exec_summary && !movie.summaries?.executive?.content && !movie.summaries?.writer?.content && (
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {movie.pcr_exec_summary}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )}
  
  {/* Synopsis Modal */}
  {movie.synopsis && (
    <div className="modal fade" id={`synopsis-${movie.id}`} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Synopsis - {movie.title}</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
          </div>
          <div className="modal-body">
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {movie.synopsis}
            </div>
          </div>
        </div>
      </div>
    </div>
  )}
  
  {/* Logline Modal */}
  {(movie.loglines?.logline || movie.loglines?.alternate) && (
    <div className="modal fade" id={`logline-${movie.id}`} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Logline{movie.loglines?.alternate ? 's' : ''} - {movie.title}</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
          </div>
          <div className="modal-body">
            {movie.loglines?.logline && (
              <div className="mb-3">
                <h6 className="fw-bold border-bottom pb-1 mb-2 text-success">
                  Primary Logline
                </h6>
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {movie.loglines.logline}
                </div>
              </div>
            )}
            {movie.loglines?.alternate && (
              <div>
                <h6 className="fw-bold border-bottom pb-1 mb-2 text-info">
                  Alternate Logline
                </h6>
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {movie.loglines.alternate}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )}
  
  {/* Full per-model PCRH Details Modal — server-rendered blade fragment,
      lazy-loaded, reusing the analysis page's per-model partials. Replaces the
      old legacy-PCR-shaped React modal (which only carried each model's exec
      blurb after projection). */}
  <div className="modal fade" id={`pcrh-full-${movie.id}`} ref={pcrhModalRef} tabIndex="-1" aria-hidden="true">
    {/* Not modal-dialog-scrollable: each tab pane owns its own scroll (the
        ref-chip jump scrolls the reader pane's body), so the outer modal
        must not add a second scrollbar. */}
    <div className="modal-dialog modal-xl">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">PCRH — {movie.title}</h5>
          <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
        </div>
        <div className="modal-body">
          {pcrhLoading && (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          )}
          {pcrhError && <div className="alert alert-warning">{pcrhError}</div>}
          {/* Server-rendered PCRH HTML is injected here imperatively (see loadPcrh)
              so its <script> executes; do not render children into this node. */}
          <div ref={pcrhBodyRef} />
        </div>
      </div>
    </div>
  </div>
</td>

      <td className="text-end">
        <MovieActions
          movie={movie}
          user={user} // Pass the user prop here
          isAnyProcessing={isAnyProcessing}
          onAnalyzeFull={onAnalyzeFull}
          onAnalyzeDeepFocus={onAnalyzeDeepFocus}
          canUsePCR={canUsePCR}
          onAnalyzePCR={onAnalyzePCR}
          onDeleteMovie={onDeleteMovie}
          onRemoveFailedMovie={onRemoveFailedMovie}
          showSuccess={showSuccess}
        />
      </td>
    </tr>
  );
};